import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, ArrowLeft, Eye, Sparkles } from 'lucide-react';
import { templates } from '../data/templates';
import GetTemplateModal from '../components/GetTemplateModal';
import { HorizonLogo } from '../components/HorizonLogo';

export default function TemplatesGallery() {
  const { t: tr } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [bought, setBought] = useState(null);
  const industries = ['all', ...new Set(templates.map(t => t.industry))];
  const filtered = filter === 'all' ? templates : templates.filter(t => t.industry === filter);

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Header */}
      <header className="py-4 px-6 lg:px-12 max-w-[1400px] mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-[#1A1816] hover:text-[#C9A96E] transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-xs font-semibold tracking-wider uppercase">{tr('common.back_to_home')}</span>
        </Link>
        <Link to="/" aria-label="Horizon — Home" className="flex items-center group">
          <HorizonLogo scale={0.45} className="block transition-opacity duration-300 group-hover:opacity-80" />
        </Link>
      </header>

      {/* Animated background */}
      <div className="fixed inset-0 pointer-events-none select-none overflow-hidden z-0">
        <motion.div className="absolute whitespace-nowrap" style={{ top: '15%' }}
          animate={{ x: ['5%', '-40%'] }} transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}>
          <span className="text-[18vw] font-bold text-[#1A1816]/[0.02] tracking-[-0.05em] font-sans leading-none">
            TEMPLATES&nbsp;&nbsp;&nbsp;WEBSITES&nbsp;&nbsp;&nbsp;TEMPLATES
          </span>
        </motion.div>
        <motion.div className="absolute whitespace-nowrap" style={{ bottom: '10%' }}
          animate={{ x: ['-30%', '5%'] }} transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}>
          <span className="text-[14vw] font-serif italic text-[#C9A96E]/[0.02] tracking-[-0.02em] leading-none">
            ready&nbsp;&nbsp;to&nbsp;&nbsp;launch&nbsp;&nbsp;ready&nbsp;&nbsp;to&nbsp;&nbsp;launch
          </span>
        </motion.div>
      </div>

      {/* Hero */}
      <section className="relative z-10 pt-12 pb-8 px-6 lg:px-12 max-w-[1400px] mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-[1px] bg-[#C9A96E]" />
            <span className="text-[9px] font-semibold tracking-[0.3em] uppercase text-[#8A857D]">{tr('common.premium_templates')}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-sans font-bold text-[#1A1816] leading-tight max-w-3xl">
            Ready-to-launch <span className="font-serif italic text-[#C9A96E]">websites</span> for your business
          </h1>
          <p className="mt-4 text-sm sm:text-base text-[#8A857D] max-w-xl leading-relaxed">
            Browse our collection of professionally designed, fully responsive landing pages. Choose your template and we will customize it for your brand.
          </p>
        </motion.div>

        {/* Filter */}
        <motion.div className="mt-8 flex flex-wrap gap-2"
          initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          {industries.map((ind) => (
            <button key={ind} onClick={() => setFilter(ind)}
              className={`px-4 py-2 text-[10px] font-semibold tracking-wider uppercase transition-all duration-300 ${
                filter === ind ? 'bg-[#1A1816] text-[#FAF8F5]' : 'border border-[#1A1816]/10 text-[#8A857D] hover:border-[#C9A96E] hover:text-[#C9A96E]'
              }`}>
              {ind === 'all' ? 'All Templates' : ind}
            </button>
          ))}
        </motion.div>
      </section>

      {/* Grid */}
      <section className="relative z-10 px-6 lg:px-12 pb-20 max-w-[1400px] mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filtered.map((t, i) => (
            <motion.div key={t.id}
              data-testid={`template-card-${t.id}`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className="group"
            >
              <div className="block">
                {/* Preview — clickable goes to detail */}
                <Link to={`/templates/${t.id}`} className="block">
                  <div className="relative overflow-hidden aspect-[4/3] bg-[#F0EDE8] border border-[#1A1816]/5">
                    {t.thumb ? (
                      <img src={t.thumb} alt={t.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${t.color}15, ${t.color}05)` }}>
                        <div className="text-center">
                          <span className="text-6xl font-serif italic" style={{ color: t.color + '30' }}>{t.name[0]}</span>
                          <p className="text-xs font-semibold tracking-wider uppercase mt-2" style={{ color: t.color + '80' }}>{t.name}</p>
                        </div>
                      </div>
                    )}

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-[#1A1816]/80 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                      <div className="flex items-center gap-2 text-[#FAF8F5] text-xs font-semibold tracking-wider uppercase">
                        <Eye className="w-4 h-4" /> View Details
                      </div>
                    </div>

                    {/* Status badge */}
                    {t.status === 'live' ? (
                      <div className="absolute top-3 right-3 flex items-center gap-1 bg-[#C9A96E] text-[#1A1816] px-2.5 py-1 text-[9px] font-semibold tracking-wider uppercase">
                        <Sparkles className="w-3 h-3" /> Live Preview
                      </div>
                    ) : (
                      <div className="absolute top-3 right-3 bg-[#1A1816]/50 backdrop-blur-sm text-[#FAF8F5] px-2.5 py-1 text-[9px] font-semibold tracking-wider uppercase">
                        Coming Soon
                      </div>
                    )}

                    {/* Signature tier badge */}
                    {t.tier === 'signature' && (
                      <div className="absolute top-3 left-3 bg-[#1A1816] text-[#C9A96E] px-2.5 py-1 text-[9px] font-bold tracking-[0.2em] uppercase border border-[#C9A96E]/40">
                        ✦ Signature
                      </div>
                    )}
                    {/* Premium tier badge */}
                    {t.tier === 'premium' && (
                      <div className="absolute top-3 left-3 bg-[#FAF8F5] text-[#1A1816] px-2.5 py-1 text-[9px] font-bold tracking-[0.2em] uppercase border border-[#1A1816]/30">
                        ◆ Premium
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="mt-4 flex items-start justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-[#1A1816] group-hover:text-[#C9A96E] transition-colors">{t.name}</h3>
                      <p className="text-xs text-[#8A857D] mt-0.5">{t.industry}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-serif italic text-[#C9A96E]">{t.price}</p>
                      <p className="text-[9px] text-[#8A857D] tracking-wide">Starting from</p>
                    </div>
                  </div>
                </Link>

                {/* CTA row — Get this template + Live preview */}
                <div className="mt-3 flex items-stretch gap-2">
                  <Link to={`/templates/${t.id}/preview`} target="_blank" rel="noopener noreferrer" data-testid={`gallery-preview-${t.id}`} className="flex-1 bg-[#1A1816] text-[#FAF8F5] py-2.5 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#C9A96E] transition-colors flex items-center justify-center gap-2">
                    <Eye className="w-3.5 h-3.5" />
                    {tr('common.view_live')}
                  </Link>
                  <button onClick={() => setBought(t)} data-testid={`gallery-buy-${t.id}`} className="border border-[#1A1816]/20 hover:border-[#C9A96E] hover:bg-[#C9A96E]/10 px-4 flex items-center justify-center gap-2 transition-colors font-mono text-[10px] tracking-[0.32em] uppercase text-[#1A1816]" aria-label={`${tr('common.contact')} ${t.name}`}>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{tr('common.contact')}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div className="mt-16 text-center"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
          <p className="text-sm text-[#8A857D] mb-4">{tr('common.need_custom')}</p>
          <Link to="/#contact"
            className="inline-flex items-center gap-2 bg-[#1A1816] text-[#FAF8F5] px-8 py-3.5 text-[10px] font-semibold tracking-wider uppercase hover:bg-[#C9A96E] hover:text-[#1A1816] transition-colors">
            {tr('common.request_custom')} <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </motion.div>
      </section>
      {bought && <GetTemplateModal template={bought} onClose={() => setBought(null)} />}
    </div>
  );
}

